import { useEffect, useRef, useState } from 'react';
import { useMediaQuery } from '@mui/material';
import {
  isSpringSettled,
  SpringParams,
  SpringState,
  stepSpring,
} from './spring';

export type KineticNumberOptions = {
  /** Disables interpolation and immediately returns the target value. */
  animate?: boolean;
  /** Spring stiffness. Higher values create a snappier transition. */
  stiffness?: number;
  /** Damping applied to the spring. Higher values reduce oscillation. */
  damping?: number;
  /** Simulated mass. Higher values create a slower transition. */
  mass?: number;
  /** Distance and velocity threshold used to settle at the target. */
  precision?: number;
  /** Allows the displayed value to pass its target before settling. */
  allowOvershoot?: boolean;
  /** Optional callback invoked for every displayed value. */
  onUpdate?: (value: number) => void;
};

const DEFAULT_SPRING_PARAMS: SpringParams = {
  stiffness: 50,
  damping: 15,
  mass: 1.6,
  precision: 0.001,
  allowOvershoot: false,
};

/**
 * Transitions a numeric value with spring physics while respecting reduced-motion preferences.
 */
export const useKineticNumber = (
  value: number,
  {
    animate = true,
    stiffness = DEFAULT_SPRING_PARAMS.stiffness,
    damping = DEFAULT_SPRING_PARAMS.damping,
    mass = DEFAULT_SPRING_PARAMS.mass,
    precision = DEFAULT_SPRING_PARAMS.precision,
    allowOvershoot = DEFAULT_SPRING_PARAMS.allowOvershoot,
    onUpdate,
  }: KineticNumberOptions = {},
): number => {
  const prefersReducedMotion = useMediaQuery(
    '(prefers-reduced-motion: reduce)',
    { noSsr: true },
  );
  const canRequestAnimationFrame =
    typeof globalThis.requestAnimationFrame === 'function';
  const shouldAnimate =
    animate &&
    !prefersReducedMotion &&
    canRequestAnimationFrame &&
    Number.isFinite(value);
  const [displayValue, setDisplayValue] = useState(value);
  const stateRef = useRef<SpringState>({
    position: value,
    velocity: 0,
    target: value,
  });
  const rafRef = useRef<number | null>(null);
  const lastTimeRef = useRef<number | null>(null);
  const paramsRef = useRef<SpringParams>({
    stiffness,
    damping,
    mass,
    precision,
    allowOvershoot,
  });
  const onUpdateRef = useRef(onUpdate);

  paramsRef.current = { stiffness, damping, mass, precision, allowOvershoot };
  onUpdateRef.current = onUpdate;

  useEffect(() => {
    if (!shouldAnimate) {
      if (rafRef.current != null) cancelAnimationFrame(rafRef.current);
      stateRef.current = { position: value, velocity: 0, target: value };
      rafRef.current = null;
      lastTimeRef.current = null;
      setDisplayValue(value);
      onUpdateRef.current?.(value);
      return;
    }

    /**
     * Advances the active spring and schedules the next frame until settled.
     */
    const tick = (time: number) => {
      const state = stateRef.current;
      const params = paramsRef.current;

      if (lastTimeRef.current == null) lastTimeRef.current = time;

      // Limit elapsed time so returning to a backgrounded tab cannot jump the spring.
      const elapsedSeconds = Math.min(
        (time - lastTimeRef.current) / 1000,
        0.032,
      );
      lastTimeRef.current = time;
      stepSpring(state, elapsedSeconds, params);

      if (isSpringSettled(state, params.precision)) {
        state.position = state.target;
        state.velocity = 0;
        setDisplayValue(state.target);
        onUpdateRef.current?.(state.target);
        rafRef.current = null;
        lastTimeRef.current = null;
        return;
      }

      setDisplayValue(state.position);
      onUpdateRef.current?.(state.position);
      rafRef.current = requestAnimationFrame(tick);
    };

    stateRef.current.target = value;
    if (rafRef.current == null) rafRef.current = requestAnimationFrame(tick);
  }, [shouldAnimate, value]);

  useEffect(
    () => () => {
      if (rafRef.current != null) cancelAnimationFrame(rafRef.current);
    },
    [],
  );

  return shouldAnimate ? displayValue : value;
};
